import { useEffect, useRef, useState } from "react";
import { useRecoilState } from "recoil";
import Calendar from "react-calendar";
import React from "react";
import { filtersAtom } from "../state/filter";
import { dashboardDataAtom } from "../state/dashboardData";
import { useAuth } from "./AuthContext";
import { API_BASE_URL } from "../config/api";

interface MemberDetails {
    securedstichRevenueShare: number;
    clientRevenueShare: number;
    companyName: string;
}

function updateUrlParams(filters: { productType: any; member: any; fromDate: any; toDate: any }) {
    const params = new URLSearchParams();
    params.set("productType", filters.productType);
    if (filters.member) params.set("member", filters.member.toString());
    params.set("fromDate", filters.fromDate.toISOString());
    params.set("toDate", filters.toDate.toISOString());
    window.history.replaceState({}, "", `?${params.toString()}`);
}

const INPUT =
    "w-full px-3 py-2 bg-[#1a1a1a] border border-[#2d2d2d] rounded text-sm text-gray-200 focus:outline-none focus:border-blue-600 transition-colors";

const LABEL = "block text-xs text-gray-500 font-medium uppercase tracking-wide mb-1";

export default function DashboardFilters() {
    const { user } = useAuth();
    const [filters, setFilters] = useRecoilState(filtersAtom);
    const [productOptions] = useState<string[]>([]);
    const [showFromCalendar, setShowFromCalendar] = useState(false);
    const [showToCalendar, setShowToCalendar] = useState(false);
    const [dashboardData, setDashboardData] = useRecoilState(dashboardDataAtom);
    const [loading, setLoading] = useState(false);
    const [memberDetails, setMemberDetails] = useState<MemberDetails | null>(null);
    const [members, setMembers] = useState<{ memberUuid: string; companyName: string }[]>([]);
    const wrapperRef = useRef(null);
    const fromCalendarRef = useRef(null);
    const toCalendarRef = useRef(null);

    const isAdmin = user?.userName === "omkark@securedstitch.com";

    // Fetch all members list (admin only)
    useEffect(() => {
        if (!isAdmin) return;
        fetch(`${API_BASE_URL}/api/member`)
            .then((r) => r.json())
            .then((data) => setMembers(data))
            .catch(() => {});
    }, [isAdmin]);

    // Fetch member details (non-admin only)
    useEffect(() => {
        if (isAdmin) return;
        const memberUuid = localStorage.getItem("memberUuid");
        if (!memberUuid) return;

        fetch(`${API_BASE_URL}/api/crd/memberDetails`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ memberUuid }),
        })
            .then((res) => res.json())
            .then((data) => setMemberDetails(data))
            .catch((error) => console.error("Error fetching member details:", error));
    }, [isAdmin]);

    /**
     * ✅ Fetch dashboard data (non-admin only) — NO date filter applied here.
     *
     * We store the FULL unfiltered dataset in dashboardDataAtom.
     * Downstream components (SummaryGrid, RecentTransactions, etc.) apply
     * their own date filter.
     *
     * Reason: cancellation reversal clones may be dated OUTSIDE the current
     * filter range but are still needed to correctly pair with cancelled
     * sales that ARE in the range. If we filter here, downstream components
     * would lose the ability to detect "this cancelled sale has a reversal
     * elsewhere in time".
     *
     * No need to re-fetch when fromDate/toDate change — the full dataset is
     * already loaded. Only refetch on user change.
     */
    useEffect(() => {
        if (isAdmin) return;

        const memberUuid = localStorage.getItem("memberUuid");
        if (!memberUuid) return;

        setLoading(true);

        fetch(`${API_BASE_URL}/api/crd/sales`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ memberUuid }),
        })
            .then((res) => res.json())
            .then((data) => {
                if (!Array.isArray(data)) {
                    setDashboardData([]);
                    return;
                }
                // ✅ Store the full unfiltered dataset
                setDashboardData(data);
            })
            .catch(() => setDashboardData([]))
            .finally(() => setLoading(false));
    }, [setDashboardData, isAdmin]);

    // Close calendars on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !(wrapperRef.current as any).contains(event.target)) {
                setShowFromCalendar(false);
                setShowToCalendar(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const getCalendarPosition = (calendarRef: React.RefObject<HTMLDivElement>) => {
        if (typeof window === "undefined") return {};
        const isMobile = window.innerWidth < 768;
        if (isMobile) {
            return {
                position: "fixed" as const,
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 9999,
            };
        }
        return {};
    };

    const calendarClass = `
        !bg-[#1a1a1a] !text-gray-200 rounded p-2 sm:p-3 !border-none
        [&_.react-calendar__tile]:text-xs [&_.react-calendar__tile]:sm:text-sm
        [&_.react-calendar__tile]:p-2 [&_.react-calendar__tile]:sm:p-3
        [&_.react-calendar__tile]:rounded [&_.react-calendar__tile]:transition-colors
        [&_.react-calendar__tile--active]:!bg-blue-600 [&_.react-calendar__tile--active]:!text-white
        [&_.react-calendar__tile:hover]:!bg-[#2d2d2d]
        [&_.react-calendar__navigation__label]:text-sm
        [&_.react-calendar__navigation__label]:sm:text-base
        [&_.react-calendar__navigation__label]:text-gray-200
        [&_.react-calendar__navigation__arrow]:text-gray-400
        [&_.react-calendar__navigation__arrow:hover]:!bg-[#2d2d2d]
        [&_.react-calendar__navigation__arrow]:rounded
        [&_.react-calendar__month-view__weekdays]:text-xs
        [&_.react-calendar__month-view__weekdays]:sm:text-sm
        [&_.react-calendar__month-view__weekdays]:text-gray-500
        [&_.react-calendar__month-view__days__day--neighboringMonth]:text-gray-600
        max-w-[90vw] md:max-w-none
    `;

    return (
        <>
            {/* Mobile backdrop */}
            {(showFromCalendar || showToCalendar) && (
                <div
                    className="fixed inset-0 bg-black/60 z-40 md:hidden"
                    onClick={() => {
                        setShowFromCalendar(false);
                        setShowToCalendar(false);
                    }}
                />
            )}

            <div ref={wrapperRef}>
                <div className="bg-[#111111] border border-[#2d2d2d] rounded p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Product Type */}
                        <div>
                            <label className={LABEL}>Product Type</label>
                            <select
                                value={filters.productType}
                                onChange={(e) => {
                                    const updated = { ...filters, productType: e.target.value };
                                    setFilters(updated);
                                    updateUrlParams(updated);
                                }}
                                className={INPUT}
                            >
                                <option value="All">All</option>
                                {productOptions.map((type) => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* From Date */}
                        <div className="relative">
                            <label className={LABEL}>From</label>
                            <input
                                readOnly
                                value={filters.fromDate.toLocaleDateString()}
                                onClick={() => {
                                    setShowFromCalendar(!showFromCalendar);
                                    setShowToCalendar(false);
                                }}
                                className={`${INPUT} cursor-pointer`}
                            />
                            {showFromCalendar && (
                                <div
                                    ref={fromCalendarRef}
                                    className="absolute z-50 bg-[#1a1a1a] mt-1 rounded border border-[#2d2d2d] shadow-lg"
                                    style={getCalendarPosition(fromCalendarRef)}
                                >
                                    <Calendar
                                        onChange={(date) => {
                                            if (date instanceof Date) {
                                                const updated = { ...filters, fromDate: date };
                                                setFilters(updated);
                                                updateUrlParams(updated);
                                                setShowFromCalendar(false);
                                            }
                                        }}
                                        value={filters.fromDate}
                                        calendarType="gregory"
                                        className={calendarClass}
                                        tileClassName={() => ""}
                                    />
                                </div>
                            )}
                        </div>

                        {/* To Date */}
                        <div className="relative">
                            <label className={LABEL}>To</label>
                            <input
                                readOnly
                                value={filters.toDate.toLocaleDateString()}
                                onClick={() => {
                                    setShowToCalendar(!showToCalendar);
                                    setShowFromCalendar(false);
                                }}
                                className={`${INPUT} cursor-pointer`}
                            />
                            {showToCalendar && (
                                <div
                                    ref={toCalendarRef}
                                    className="absolute z-50 bg-[#1a1a1a] mt-1 rounded border border-[#2d2d2d] shadow-lg"
                                    style={getCalendarPosition(toCalendarRef)}
                                >
                                    <Calendar
                                        onChange={(date) => {
                                            if (date instanceof Date) {
                                                const updated = { ...filters, toDate: date };
                                                setFilters(updated);
                                                updateUrlParams(updated);
                                                setShowToCalendar(false);
                                            }
                                        }}
                                        value={filters.toDate}
                                        calendarType="gregory"
                                        className={calendarClass}
                                        tileClassName={() => ""}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Member */}
                        <div>
                            <label className={LABEL}>Member</label>
                            {isAdmin ? (
                                <select
                                    className={INPUT}
                                    value={filters.member}
                                    onChange={(e) => {
                                        const updated = { ...filters, member: e.target.value };
                                        setFilters(updated);
                                        updateUrlParams(updated);
                                    }}
                                >
                                    <option value="">All Members</option>
                                    {members.map((m) => (
                                        <option key={m.memberUuid} value={m.memberUuid}>
                                            {m.companyName}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <div className={`${INPUT} truncate`}>{memberDetails?.companyName || "Loading..."}</div>
                            )}
                        </div>
                    </div>

                    {/* Status */}
                    {(loading || dashboardData.length === 0) && (
                        <div className="mt-3 pt-3 border-t border-[#1f1f1f]">
                            {loading ? (
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 border-2 border-gray-600 border-t-gray-300 rounded-full animate-spin" />
                                    <span className="text-xs text-gray-500">Loading data…</span>
                                </div>
                            ) : (
                                <p className="text-xs text-gray-600">No records found for the selected period.</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}